import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  DayPicker,
  useDayPicker,
  type Matcher,
  type MonthCaptionProps,
} from "react-day-picker";

import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  /**
   * When true, the calendar expands to fill its container: month width, weekday
   * cells, and day cells become fractional instead of a fixed 32px. Use this
   * inside mobile drawers where the calendar should feel full-width and touch-
   * friendly rather than sitting in a 256px island of whitespace.
   */
  fluid?: boolean;
};

/** How far either side of today the year view reaches, absent a caller's range. */
const YEAR_SPAN = 30;

/** The first of `d`'s month. */
const monthOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

/**
 * The first and last days a caller's `disabled` matchers leave pickable, read
 * from their open-ended `{ before }` and `{ after }` matchers — how `DatePicker`
 * passes its `minDate`/`maxDate`, and so how a picker locked to the data's
 * range (Market Intelligence's) says what that range is. Any other matcher
 * (a date, a span, a function) disables days inside the range, not its ends.
 */
function pickableRange(disabled: Matcher | Matcher[] | undefined): {
  first?: Date;
  last?: Date;
} {
  const matchers = disabled === undefined ? [] : Array.isArray(disabled) ? disabled : [disabled];
  let first: Date | undefined;
  let last: Date | undefined;
  for (const m of matchers) {
    if (typeof m !== "object" || m === null || m instanceof Date || Array.isArray(m)) continue;
    const before = "before" in m ? m.before : undefined;
    const after = "after" in m ? m.after : undefined;
    // `{ before, after }` together is a span disabled INSIDE the range.
    if (before instanceof Date && after === undefined) {
      first = first && first > before ? first : before;
    }
    if (after instanceof Date && before === undefined) {
      last = last && last < after ? last : after;
    }
  }
  return { first, last };
}

/**
 * Token-themed wrapper around react-day-picker. Mirrors shadcn's Calendar but
 * swaps the default neutral palette for our NorthStar tokens so it matches
 * both light (warm cream) and dark themes without further theming.
 *
 * **The caption opens a year view.** "June 2021 ▾" sits at the left with the
 * month arrows grouped at the right; clicking it swaps the day grid for a grid
 * of years, and picking one returns to the same month in that year. A date
 * years away is two clicks rather than a click per month. The arrows still
 * page one month at a time.
 *
 * The year view lies OVER the day grid, which stays mounted but invisible, so
 * the calendar keeps its size and its popover does not re-pin as it switches.
 *
 * **A picker locked to a range is held to it.** When the days before or after
 * some date are disabled (`DatePicker`'s `minDate`/`maxDate`, as a Market
 * Intelligence chart passes its data's extent), the arrows stop at those
 * months and the year view offers only those years, rather than decades with
 * nothing to pick in them. `startMonth`/`endMonth`, when passed, win.
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fluid = false,
  month: controlledMonth,
  defaultMonth,
  onMonthChange,
  startMonth,
  endMonth,
  ...props
}: CalendarProps) {
  // The shown month is held here (not left to react-day-picker) so the year
  // view can move it. A caller that controls `month` still does.
  const [ownMonth, setOwnMonth] = useState<Date>(() => defaultMonth ?? new Date());
  const month = controlledMonth ?? ownMonth;
  const goTo = (next: Date) => {
    setOwnMonth(next);
    onMonthChange?.(next);
  };

  // The months there is anything to pick in: the caller's own bounds, else
  // the ends of what its `disabled` matchers leave pickable.
  const pickable = pickableRange(props.disabled);
  const navStart = startMonth ?? pickable.first;
  const navEnd = endMonth ?? pickable.last;
  const clampToRange = (d: Date) => {
    if (navStart && d < monthOf(navStart)) return monthOf(navStart);
    if (navEnd && d > monthOf(navEnd)) return monthOf(navEnd);
    return d;
  };

  // The caption a year view was opened from: its month and its place among the
  // months shown, so a two-month range picker lands where the reader clicked.
  const [yearsFrom, setYearsFrom] = useState<{
    month: Date;
    index: number;
    label: string;
  } | null>(null);

  // Shared cell sizing. Fixed 32px squares on desktop (compact popover); on
  // mobile we let each day grow to 1/7th of the calendar width with a taller
  // touch target so it doesn't feel cramped inside a full-width drawer.
  const cellBase = fluid ? "flex-1 aspect-square" : "w-8 h-8";
  const weekdayBase = fluid ? "flex-1" : "w-8";

  return (
    <div className={cn("relative", fluid && "w-full")}>
      <DayPicker
        showOutsideDays={showOutsideDays}
        // Always render 6 week-rows so every month is the same height. Without
        // this, months span 4–6 rows and the calendar's height changes as you
        // page — inside a popover that re-pins to its content, the picker
        // visibly "bounces" between months (AB#7178).
        fixedWeeks
        month={month}
        onMonthChange={goTo}
        startMonth={navStart}
        endMonth={navEnd}
        className={cn(
          "p-2 text-sm text-foreground",
          fluid && "w-full",
          // Hidden, not unmounted, under the year view: it holds the size.
          yearsFrom && "invisible",
          className,
        )}
        components={{
          Chevron: ({ orientation }) =>
            orientation === "left" ? <ChevronLeft size={14} /> : <ChevronRight size={14} />,
          MonthCaption: (captionProps: MonthCaptionProps) => (
            <YearViewCaption
              {...captionProps}
              onOpen={(m, index, label) => setYearsFrom({ month: m, index, label })}
            />
          ),
        }}
        classNames={{
          months: "relative flex flex-col sm:flex-row gap-4",
          month: cn("flex flex-col gap-3", fluid && "w-full"),
          month_caption: "flex items-center h-8",
          // The month arrows sit together at the right of the caption row, the
          // caption itself at the left, as in the picker the desk asked to
          // match. Absolutely positioned over the outer `months` wrapper, so a
          // two-month range picker has one pair, at its far right.
          nav: "absolute top-0 right-0 h-8 flex items-center gap-0.5 pointer-events-none",
          button_previous:
            "h-7 w-7 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:bg-row-hover hover:text-foreground pointer-events-auto",
          button_next:
            "h-7 w-7 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:bg-row-hover hover:text-foreground pointer-events-auto",
          month_grid: "w-full border-collapse mt-2",
          weekdays: "flex",
          weekday: cn(
            "h-7 flex items-center justify-center text-xs uppercase tracking-eyebrow text-text-dim font-semibold",
            weekdayBase,
          ),
          week: "flex w-full mt-1",
          // Day cell (the <td>). We paint the range band here as a flat,
          // edge-to-edge fill. Only start/end cells get rounded corners — the
          // cells between them are fully square so the band is continuous.
          day: cn("relative p-0 text-center", cellBase),
          // Day button (the clickable element inside the cell). Square by
          // default so the range band isn't interrupted. Hover on non-range
          // days uses row-hover; range/selected styles override further down.
          day_button: cn(
            "rounded-none text-sm text-foreground hover:bg-row-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-mid focus-visible:ring-inset",
            // Fixed-size calendar keeps a square 32×32 button; fluid lets the
            // button fill its (fractional-width, aspect-square) parent cell.
            fluid ? "h-full w-full" : "h-8 w-8",
          ),
          today: "[&>button]:ring-1 [&>button]:ring-primary-mid [&>button]:ring-inset",
          outside: "text-text-dim",
          disabled: "text-text-dim opacity-40",
          // Range — shadcn-style dual tone:
          //   · middle: soft teal tint (teal-mid ~25%) with teal text
          //   · start / end: solid teal pills with white text
          // Cell-level background paints the band continuously across gaps;
          // rounded corners only on the outermost edges of start and end.
          range_middle:
            "bg-primary-mid [&>button]:bg-transparent [&>button]:text-primary [&>button]:hover:bg-transparent",
          range_start:
            "bg-primary-mid rounded-l-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:font-semibold [&>button]:hover:bg-primary [&>button]:rounded-md",
          range_end:
            "bg-primary-mid rounded-r-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:font-semibold [&>button]:hover:bg-primary [&>button]:rounded-md",
          // Single-day selection or start === end: full pill on both sides.
          selected:
            "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:font-semibold [&>button]:rounded-md",
          ...classNames,
        }}
        {...props}
      />
      {yearsFrom && (
        <YearView
          shown={yearsFrom.month}
          label={yearsFrom.label}
          from={navStart?.getFullYear() ?? new Date().getFullYear() - YEAR_SPAN}
          to={navEnd?.getFullYear() ?? new Date().getFullYear() + YEAR_SPAN}
          onClose={() => setYearsFrom(null)}
          onPick={(year) => {
            // The same month in the year picked, less the caption's place, so
            // the month clicked lands where it was in a two-month picker. Held
            // inside the range: June in a year whose data ends in March opens on
            // March.
            goTo(
              clampToRange(new Date(year, yearsFrom.month.getMonth() - yearsFrom.index, 1)),
            );
            setYearsFrom(null);
          }}
        />
      )}
    </div>
  );
}

/** The caption button's look, shared by the day view and the year view. */
const CAPTION_BUTTON =
  "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-sm font-semibold text-foreground hover:bg-row-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-mid";

/**
 * A month's caption as a button: "June 2021 ▾", which opens the year view.
 * Uses react-day-picker's own caption formatter, so it reads as it did.
 */
function YearViewCaption({
  calendarMonth,
  displayIndex,
  onOpen,
  ...rest
}: MonthCaptionProps & { onOpen: (month: Date, index: number, label: string) => void }) {
  const { formatters } = useDayPicker();
  const label = formatters.formatCaption(calendarMonth.date);
  return (
    <div {...rest}>
      <button
        type="button"
        onClick={() => onOpen(calendarMonth.date, displayIndex, label)}
        aria-label={`${label}, choose a year`}
        className={CAPTION_BUTTON}
      >
        {label}
        <ChevronDown size={14} className="text-muted-foreground" />
      </button>
    </div>
  );
}

/**
 * The year view: every year in range, four to a row, the shown year filled.
 * Opens scrolled to the shown year. Its caption closes it again, as in the
 * picker it is modelled on; Escape still closes the whole popover.
 */
function YearView({
  shown,
  label,
  from,
  to,
  onClose,
  onPick,
}: {
  shown: Date;
  /** The caption it was opened from, as that caption printed it. */
  label: string;
  from: number;
  to: number;
  onClose: () => void;
  onPick: (year: number) => void;
}) {
  const shownYear = shown.getFullYear();
  // A shown year outside the range is still offered, so it shows as picked.
  const first = Math.min(from, shownYear);
  const last = Math.max(to, shownYear);
  const years = Array.from({ length: last - first + 1 }, (_, i) => first + i);

  const current = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    current.current?.scrollIntoView?.({ block: "center" });
    current.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="absolute inset-0 flex flex-col gap-2 p-2">
      <div className="flex h-8 items-center">
        <button
          type="button"
          onClick={onClose}
          aria-label={`${label}, back to days`}
          className={CAPTION_BUTTON}
        >
          {label}
          <ChevronDown size={14} className="rotate-180 text-muted-foreground" />
        </button>
      </div>
      <div
        role="listbox"
        aria-label="Years"
        className="grid flex-1 auto-rows-min grid-cols-4 gap-1 overflow-y-auto"
      >
        {years.map((year) => {
          const selected = year === shownYear;
          return (
            <button
              key={year}
              ref={selected ? current : undefined}
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => onPick(year)}
              className={cn(
                "rounded-full py-1.5 text-sm font-semibold tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-mid",
                selected
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-row-hover",
              )}
            >
              {year}
            </button>
          );
        })}
      </div>
    </div>
  );
}
